<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import DropdownButton from "@/components/ui/DropdownButton.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import TextInput from "@/components/ui/TextInput.vue";
import UserAvatar from "@/components/ui/UserAvatar.vue";
import { getUserFriendlyName, type TenantDto, type UserDto } from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { format } from "date-fns/format";

const api = useApi();
const route = useRoute();
const router = useRouter();
const isLoading = ref(true);
const isLoadingMembers = ref(true);
const tenant = ref<TenantDto>();
const members = shallowRef(new Map<string, UserDto>());

const tenantId = computed(() => parseInt(route.params.tenantId as string));

function formatDatetime(dt: string) {
  return format(new Date(dt), "d MMMM yyyy hh:mm");
}

async function fetchTenant() {
  isLoading.value = true;
  const t = await api.fetchTenant(tenantId.value);
  if (t !== null) {
    tenant.value = t;
  } else {
    router.replace({ name: RouteNames.NotFound });
  }
  isLoading.value = false;
}

async function fetchMembers() {
  isLoadingMembers.value = true;
  if (tenant.value && tenant.value.members) {
    const idsToFetch = new Set<string>();
    tenant.value.members.forEach((m) => {
      idsToFetch.add(m.user_id);
      if (m.invited_by !== undefined) {
        idsToFetch.add(m.invited_by);
      }
    });
    const results = new Map<string, UserDto>();
    for (const id of idsToFetch) {
      const u = await api.fetchUser(id);
      if (u) {
        results.set(id, u);
      }
    }
    members.value = results;
  }
  isLoadingMembers.value = false;
}

function getMemberFeriendlyName(member: UserDto) {
  return getUserFriendlyName(member);
}

function getInviterFriendlyName(userId: string) {
  const inviter = members.value.get(userId);
  return inviter ? getMemberFeriendlyName(inviter) : "";
}

onMounted(async () => {
  await fetchTenant();
  await fetchMembers();
});
</script>

<template>
  <DefaultPageLayout :breadcrumb-items="[{ label: 'Tenants' }, { label: 'Settings' }]">
    <div class="tenant-detail-view">
      <RouterLink :to="{ name: RouteNames.Dispatch }" class="back">
        <i class="icon icon-arrow-left" /> Tenants
      </RouterLink>

      <Transition name="fade">
        <SkoreSpinner v-if="isLoading" />
        <div v-else-if="tenant !== undefined" class="wrapper">
          <header>
            <h1>Profile</h1>
            <h2>Manage tenant profile.</h2>
          </header>

          <div class="profile-element">
            <div>Public ID</div>
            <TextInput v-model="tenant.public_id" disabled />
          </div>

          <div class="profile-element">
            <h1>Name</h1>
            <TextInput v-model="tenant.name" disabled />
          </div>

          <header class="members">
            <div>
              <h1>Members</h1>
              <h2>Manage members inside your organisation.</h2>
            </div>
            <SimpleButton
              icon-left="icon-new-user"
              label="Invite member"
              kind="tertiary"
              disabled
            />
          </header>

          <SkoreSpinner v-if="isLoadingMembers" />
          <table v-else class="tabular-list members">
            <thead>
              <tr>
                <th>User</th>
                <th>Mail</th>
                <th>Joined</th>
                <th>Invited by</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="member in tenant.members" :key="member.user_id">
                <td>
                  <UserAvatar :name="getMemberFeriendlyName(members.get(member.user_id)!)" />
                </td>
                <td>{{ members.get(member.user_id)!.email }}</td>
                <td>{{ formatDatetime(tenant.created_at) }}</td>
                <td class="cell-with-actions">
                  {{ getInviterFriendlyName(member.invited_by ?? "") }}
                  <DropdownButton icon-right="icon-more-vertical" kind="tertiary" disabled>
                  </DropdownButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Transition>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.tenant-detail-view {
  .back {
    color: var(--color-text-secondary);
    font-weight: var(--font-weight-medium);
    text-decoration: none;

    i {
      padding-right: var(--spacing-8);
      vertical-align: middle;
    }
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    padding: calc(var(--spacing-32) - var(--spacing-12)) 0 var(--spacing-32) 0;
    gap: var(--spacing-32);

    h1 {
      color: var(--color-text-primary);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);

      i {
        margin-right: var(--spacing-6);
        color: var(--color-icon-tertiary);
      }
    }

    h2 {
      color: var(--color-text-secondary);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-regular);
    }

    .cell-with-actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }

    header.members {
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
